import { defineStore } from 'pinia'
import {ref, computed, nextTick} from 'vue'
import type { Conversation, Message } from '@/types'
import {
  generateId,
  generateTitle,
  getStreamResponse,
  UploadedFile
} from '@/utils/helpers'
import {getAIHistoryList, addAiRecord, deleteAIRecord, getAIcontext, renameAI} from '@/AIApi'
import { ElMessage } from 'element-plus'

export const useChatStore = defineStore('chat', () => {
  const conversations = ref<Conversation[]>([])
  const activeConversationId = ref<string | null>(null)
  const isStreaming = ref(false)
  const selectedModel = ref('QWen3')
  const isLoading = ref(false) // 加载状态
  let accountId = '' // 当前用户的 Id

  const activeConversation = computed(() =>
      conversations.value.find(c => c.id === activeConversationId.value) ?? null
  )

  const groupedConversations = computed(() => {
    const now = new Date()
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const yesterdayStart = new Date(todayStart)
    yesterdayStart.setDate(yesterdayStart.getDate() - 1)

    const groups = [
      { label: '今天', items: [] as Conversation[] },
      { label: '昨天', items: [] as Conversation[] },
      { label: '更早', items: [] as Conversation[] },
    ]

    for (const conv of conversations.value) {
      const d = new Date(conv.updatedAt)
      const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate())
      if (dayStart >= todayStart) {
        groups[0].items.push(conv)
      } else if (dayStart >= yesterdayStart) {
        groups[1].items.push(conv)
      } else {
        groups[2].items.push(conv)
      }
    }

    return groups.filter(g => g.items.length > 0)
  })

  // ==================== API 操作 ====================

  // 从 API 加载对话记录
  async function loadConversations(userId: string, preserveMessages = false) {
    isLoading.value = true
    accountId = userId

    try {
      let savedMessages: Message[] = []
      let savedConvId: string | null = null
      let savedConvTitle = ''

      if (preserveMessages && activeConversation.value) {
        savedConvId = activeConversation.value.id
        savedMessages = [...activeConversation.value.messages]
        savedConvTitle = activeConversation.value.title
        console.log('💾 保存当前对话消息，数量:', savedMessages.length)
      }

      const [error, response] = await awaitTo(getAIHistoryList({ userId }))

      if (!error && response && response.data) {
        const apiData = response.data
        const newConversations = apiData.map((item: any) => {
          const convId = item.sessionId || generateId()

          // 如果是当前活动对话且有保存的消息，保留消息
          if (preserveMessages && savedConvId && convId === savedConvId && savedMessages.length > 0) {
            console.log('✅ 保留当前对话消息，数量:', savedMessages.length)
            return {
              id: convId,
              title: savedConvTitle,
              messages: savedMessages,
              model: selectedModel.value,
              createdAt: new Date(item.createdAt),
              updatedAt: new Date(item.updatedAt),
            }
          }

          return {
            id: convId,
            title: item.title || '新对话',
            messages: [],
            model: selectedModel.value,
            createdAt: new Date(item.createdAt),
            updatedAt: new Date(item.updatedAt),
          }
        })
          // 倒序排列，最新的对话显示在最前面
       // newConversations.reverse()
        // 如果当前对话不在列表中，添加到首位
        if (preserveMessages && savedConvId && savedMessages.length > 0) {
          const exists = newConversations.find(c => c.id === savedConvId)
          if (!exists) {
            const newConv: Conversation = {
              id: savedConvId,
              title: savedConvTitle,
              messages: savedMessages,
              model: selectedModel.value,
              createdAt: new Date(),
              updatedAt: new Date(),
            }
            newConversations.unshift(newConv)
            console.log('✅ 当前对话不在列表中，已添加')
          }
        }

        conversations.value = newConversations
        console.log('✅ 加载对话记录成功，数量:', conversations.value.length)
      }
    } catch (err) {
      console.error('❌ 加载对话记录失败:', err)
    } finally {
      isLoading.value = false
    }
  }
  // 获取对话上下文
  async function loadConversationContext(id: string) {
    try {
      const [error, response] = await awaitTo(getAIcontext(id,{}))

      if (response.data && Array.isArray(response.data)) {
        console.log('✅ 加载上下文成功，消息数:', response.data)
        const conv = conversations.value.find(c => c.id === id)
        if (conv) {
          // 将 API 返回的消息转换为 Message 格式
          conv.messages = response.data.map((msg: any) => {
            const role = msg.role === 'USER' ? 'user' : 'assistant'
            let content = msg.content || ''
            return {
              id: msg.messageId || generateId(),
              role: role,
              content: content,
              timestamp: new Date(msg.createTime || msg.timestamp || Date.now()),
              isStreaming: false,
            }
          })

          console.log('✅ 加载上下文成功，消息数:', conv.messages.length)
        }
      }
    } catch (err) {
      console.error('❌ 加载上下文失败:', err)
    }
  }

  // 删除对话（调用 API）
  async function deleteConversationApi(chatId: string) {
    try {
      await awaitTo(deleteAIRecord(chatId))
      deleteConversation(chatId)
      console.log('✅ 对话已删除:', chatId)
    } catch (err) {
      console.error('❌ 删除对话失败:', err)
      throw err
    }
  }

  // ==================== 本地操作 ====================

  // 删除对话（本地）
  function deleteConversation(id: string) {
    const idx = conversations.value.findIndex(c => c.id === id)
    if (idx === -1) return

    conversations.value.splice(idx, 1)

    if (activeConversationId.value === id) {
      activeConversationId.value = conversations.value[0]?.id ?? null
    }

    console.log('🗑️ 本地对话已删除:', id)
  }

  // 重命名对话
  async function renameConversation(id: string, title: string) {
    const conv = conversations.value.find(c => c.id === id)
    try {
      // 调用 API 更新会话名称
      await awaitTo(renameAI(id, { title: title }))

      // 更新本地状态
      conv.title = title
      console.log('✏️ 对话已重命名:', title)
      ElMessage.success('重命名成功')
    } catch (err) {
      console.error('❌ 重命名失败:', err)
      ElMessage.error('重命名失败，请稍后重试')
      throw err
    }
  }

  // 设置活动对话
  function setActive(id: string | null) {
    activeConversationId.value = id
    console.log('🎯 设置活动对话 ID:', id)
  }

  // 清除当前活动对话（回到欢迎页/新对话页面）
  function clearActiveConversation() {
    activeConversationId.value = null
    console.log('🔙 已清除活动对话，返回欢迎页')
  }
  // 清空所有对话数据(用于登出或token过期时)
  function clearAllConversations() {
    conversations.value = []
    activeConversationId.value = null
    console.log('🗑️ 已清空所有对话数据')
  }

  // ==================== 核心功能 ====================

  // 发送消息
  async function sendMessage(content: string, files?: UploadedFile[]) {
    if (!content.trim() || isStreaming.value) return
    if (!accountId) {
      ElMessage.warning('请先登录')
      return
    }

    let conv = activeConversation.value

    if (!conv) {
      const newId = generateId()
      const newConv: Conversation = {
        id: newId,
        title: generateTitle(content),
        messages: [],
        model: selectedModel.value,
        createdAt: new Date(),
        updatedAt: new Date(),
      }
      conversations.value.unshift(newConv)
      activeConversationId.value = newId
      // 重新获取响应式引用
      conv = activeConversation.value
      console.log('🆕 创建新对话，自动生成 ID:', newId)
    }

    const userMsg: Message = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    }
    conv.messages.push(userMsg)
    conv.updatedAt = new Date()

    if (conv.messages.length === 1) {
      conv.title = generateTitle(content)
      console.log('📝 自动生成标题:', conv.title)
    }

    const aiMsg: Message = {
      id: generateId(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    }
    conv.messages.push(aiMsg)

    const aiMsgIndex = conv.messages.length - 1
    isStreaming.value = true

    try {
      console.log('💬 使用对话 ID:', conv.id)
      console.log('🤖 当前模型:', selectedModel.value)
      console.log(' 开始调用 AI 回复接口...')

      await getStreamResponse(
          content.trim(),
          conv.id,
          (chunk) => {
            const msg = conv.messages[aiMsgIndex]
            if (msg) {
              msg.content += chunk
              console.log(' 更新消息内容，当前长度:', msg.content.length)
            }
          },
          false,
          files
      )

      console.log('✅ AI 响应完成，最终长度:', conv.messages[aiMsgIndex].content.length)
      // 流式完成后，只更新对话标题到服务端，不刷新列表
     /* if (conv.messages.length === 2) {
        await awaitTo(renameAI(conv.id, { title: conv.title }))
        console.log('✅ 对话标题已保存到服务端')
      }*/
      } catch (err) {
      console.error('❌ 发送消息异常:', err)
      if (conv.messages[aiMsgIndex]) {
        conv.messages[aiMsgIndex].content = '抱歉，处理您的请求时出现了错误，请稍后再试。'
      }
    } finally {
      if (conv.messages[aiMsgIndex]) {
        conv.messages[aiMsgIndex].isStreaming = false
      }
      isStreaming.value = false
    }
  }

  // ==================== 导出 ====================

  return {
    // 状态
    conversations,
    activeConversationId,
    activeConversation,
    groupedConversations,
    isStreaming,
    selectedModel,
    isLoading,

    // API 方法
    loadConversations,
    loadConversationContext,
    deleteConversationApi,

    // 本地方法
    deleteConversation,
    renameConversation,
    setActive,
    sendMessage,
    clearActiveConversation,
    clearAllConversations
  }
})
